import { useParams, Link } from "react-router-dom";
import { ArrowRight, Check, Clock3, Users2, Award, ShieldCheck, MapPin, Building2, Phone, Zap } from "lucide-react";
import { getCourse, SEDI, COURSE_IMAGES, HERO_IMAGE } from "@/lib/data";
import SEO, { courseSchema, faqPageSchema } from "@/components/SEO";
import CorsiBoard from "@/components/CorsiBoard";
import PreventivoWizard from "@/components/PreventivoWizard";

export default function CorsoDetail() {
    const { slug } = useParams();
    const course = getCourse(slug);
    if (!course) {
        return (
            <div className="container-7m py-32 text-center">
                <SEO title="Corso non trovato" description="Il corso richiesto non è disponibile." path={`/corsi/${slug}`} noindex />
                <h1 className="font-heading font-black text-4xl">Corso non trovato</h1>
                <Link to="/corsi" className="btn-primary mt-8">Tutti i corsi</Link>
            </div>
        );
    }

    const heroImage = COURSE_IMAGES[course.slug] || HERO_IMAGE;
    const hasLiveDates = !!course.bookingOrgSlug;

    // Programma section labels — pre-computed to avoid nested ternaries
    let programEyebrow = "Programma";
    if (course.topics) programEyebrow = "Contenuti";
    else if (course.format) programEyebrow = "Come funziona";

    const programTitle = course.format ? "Format flessibile." : "Cosa imparerai.";

    let programIntro = "Approccio pratico e concreto: clicca su ogni modulo per scoprire i dettagli.";
    if (course.topicsIntro) programIntro = course.topicsIntro;
    else if (course.format) programIntro = "Niente programma rigido. Tu ci dici i tuoi obiettivi, noi adattiamo durata e contenuti.";

    function renderProgramBody() {
        if (course.format) return <FormatBlock format={course.format} />;
        if (course.topics) return <TopicsList topics={course.topics} />;
        return (
            <ul className="divide-y divide-ink/10 border-t border-b border-ink/10">
                {course.modules.map((m, i) => (
                    <ModuleItem key={m.name} module={m} index={i} testId={`module-${i}`} />
                ))}
            </ul>
        );
    }

    const PreventivoOrDateBlock = hasLiveDates ? (
        <section id="date" className="py-20 lg:py-28 bg-[#FAFAFA] border-t border-ink/10">
            <div className="container-7m">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-10 lg:mb-14">
                    <div className="lg:col-span-7">
                        <div className="label-eyebrow text-brand">Prossime date · iscrizione online</div>
                        <h2 className="mt-4 font-heading font-black text-4xl lg:text-5xl tracking-tighter leading-tight">Scegli la tua data.</h2>
                    </div>
                    <p className="lg:col-span-5 text-readable">
                        Date confermate live dal nostro sistema di gestione. Iscrizione online con conferma immediata via email.
                    </p>
                </div>
                <CorsiBoard
                    {...(course.bookingOrgSlugs ? { orgs: course.bookingOrgSlugs } : { org: course.bookingOrgSlug })}
                    tipoNomeIncludes={course.bookingTipoNomeIncludes}
                />
            </div>
        </section>
    ) : (
        <section id="preventivo" className="py-20 lg:py-28 bg-[#FAFAFA] border-t border-ink/10">
            <div className="container-7m">
                <div className="max-w-3xl mb-10 lg:mb-14">
                    <div className="label-eyebrow text-brand">Preventivo guidato</div>
                    <h2 className="mt-4 font-heading font-black text-4xl lg:text-5xl tracking-tighter leading-tight">
                        Richiedi un preventivo per {course.title}.
                    </h2>
                    <p className="mt-5 text-readable-lg">
                        Rispondi a 5 domande e ricevi un'offerta personalizzata entro 24 ore. Senza impegno.
                        Il corso «{course.title}» è già pre-selezionato per te.
                    </p>
                </div>
                <div className="bg-white p-6 sm:p-10 lg:p-14 border border-ink/10">
                    <PreventivoWizard preselectedCourse={course.slug} idPrefix={`wizard-${course.slug}`} />
                </div>
            </div>
        </section>
    );

    return (
        <>
            <SEO
                title={`${course.fullTitle} · ${course.audienceLabel}`}
                description={`${course.tagline} ${course.duration}. ${course.certification}. ${course.subtitle}`}
                path={course.href}
                keywords={`${course.title}, ${course.audienceLabel}, corso ${course.title} Ticino, ${course.certification}`}
                ogImage={heroImage}
                structuredData={course.faq?.length ? [courseSchema(course), faqPageSchema(course.faq)] : courseSchema(course)}
                breadcrumbs={[
                    { name: "Home", path: "/" },
                    { name: "Corsi", path: "/corsi" },
                    { name: course.title, path: course.href },
                ]}
            />

            {/* HERO with image */}
            <section className="bg-white border-b border-ink/10">
                <div className="container-7m pt-10 lg:pt-14 pb-0">
                    <Link to="/corsi" className="text-xs font-mono uppercase tracking-[0.2em] text-ink-muted hover:text-brand" data-testid="course-back-link">← Tutti i corsi</Link>
                </div>
                <div className="container-7m py-10 lg:py-16">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
                        <div className="lg:col-span-7">
                            <div className="flex items-center gap-3 flex-wrap">
                                <div className="px-3 py-1.5 bg-brand text-white text-[10px] font-mono tracking-wider uppercase">{course.eyebrow}</div>
                                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-ink-muted">{course.audienceLabel}</div>
                            </div>
                            <h1 className="mt-5 font-heading font-black text-4xl sm:text-5xl lg:text-7xl tracking-tighter leading-[1.04]">{course.fullTitle}</h1>
                            <p className="mt-5 sm:mt-6 text-readable-lg max-w-2xl">{course.tagline}</p>
                            <div className="mt-8 flex gap-4 flex-wrap">
                                <a href={hasLiveDates ? "#date" : "#preventivo"} className="btn-primary" data-testid="course-cta-main">
                                    {hasLiveDates ? "Vedi prossime date" : "Richiedi preventivo"} <ArrowRight className="w-4 h-4" />
                                </a>
                                <a href="tel:+41912615199" className="btn-secondary inline-flex items-center gap-2">
                                    <Phone className="w-4 h-4" /> 091 261 51 99
                                </a>
                            </div>
                        </div>
                        <div className="lg:col-span-5">
                            <div className="aspect-[4/3] lg:aspect-[5/6] overflow-hidden bg-ink/5">
                                <img src={heroImage} alt={`${course.fullTitle} — formazione in Ticino`} className="w-full h-full object-cover" loading="eager" />
                            </div>
                        </div>
                    </div>
                </div>
                {/* Quick specs row */}
                <div className="border-t border-ink/10">
                    <div className="container-7m">
                        <div className={`grid grid-cols-2 ${course.groupSize ? "lg:grid-cols-4" : "lg:grid-cols-3"} divide-x divide-y lg:divide-y-0 divide-ink/10`}>
                            <Spec icon={Clock3} label="Durata" value={course.duration} />
                            {course.groupSize ? <Spec icon={Users2} label="Partecipanti" value={course.groupSize} /> : null}
                            <Spec icon={Award} label="Certificazione" value={course.certification} />
                            <Spec icon={ShieldCheck} label="Prezzo" value={course.price || course.priceNote} highlight={!!course.price} />
                        </div>
                    </div>
                </div>
            </section>

            {/* DATE LIVE (Samaritani) — prima del programma */}
            {hasLiveDates && PreventivoOrDateBlock}

            {/* PROGRAMMA — 3 varianti: modules accordion / format block / topics list */}
            <section className="py-20 lg:py-28 bg-[#FAFAFA]" data-testid="programma-section">
                <div className="container-7m grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <div className="label-eyebrow text-brand">{programEyebrow}</div>
                        <h2 className="mt-4 font-heading font-black text-4xl lg:text-5xl tracking-tighter leading-tight">
                            {programTitle}
                        </h2>
                        <p className="mt-6 text-ink-muted">
                            {programIntro}
                        </p>
                    </div>
                    <div className="lg:col-span-7">
                        {renderProgramBody()}
                    </div>
                </div>
            </section>

            {/* PUNTI CHIAVE */}
            <section className="py-20 lg:py-28 bg-white">
                <div className="container-7m grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-6">
                        <div className="label-eyebrow text-brand">Punti chiave</div>
                        <h2 className="mt-4 font-heading font-black text-3xl lg:text-4xl tracking-tighter leading-tight">Perché scegliere {course.title}.</h2>
                        <ul className="mt-8 space-y-4">
                            {course.bullets.map((b) => (
                                <li key={b} className="flex gap-4 items-start">
                                    <Check className="w-5 h-5 text-brand flex-shrink-0 mt-1" strokeWidth={2.5} />
                                    <span className="leading-relaxed">{b}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="lg:col-span-5 lg:col-start-8">
                        {course.info ? (
                            <div className="bg-ink text-white p-8 lg:p-10 space-y-6">
                                <div className="label-eyebrow !text-brand">Info utili</div>
                                {course.info.map((it) => (
                                    <div key={it.label}>
                                        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-brand">{it.label}</div>
                                        <div className="mt-1 text-sm text-white/80 leading-relaxed">{it.value}</div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="bg-ink text-white p-8 lg:p-10">
                                <div className="label-eyebrow !text-brand">Certificazione</div>
                                <h3 className="mt-4 font-heading font-bold text-2xl leading-tight">{course.certification}</h3>
                                <p className="mt-4 text-sm text-white/70 leading-relaxed">Rilasciato al termine del corso, dopo superamento delle prove pratiche.</p>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* PREVENTIVO INLINE — solo per corsi SENZA date live (per Samaritani è già renderizzato sopra) */}
            {!hasLiveDates && PreventivoOrDateBlock}

            {/* GROUP NOTE — corsi privati/gruppi (es. Samaritani) */}
            {course.groupNote ? (
                <section className="py-16 lg:py-20 bg-white border-t border-ink/10" data-testid="group-note">
                    <div className="container-7m">
                        <div className="bg-brand text-white p-8 sm:p-10 lg:p-14 flex flex-col lg:flex-row gap-8 lg:items-center lg:justify-between">
                            <div className="flex-1 max-w-2xl">
                                <h3 className="font-heading font-black text-3xl lg:text-4xl tracking-tighter leading-tight">{course.groupNote.title}</h3>
                                <p className="mt-4 text-white/90 leading-relaxed">{course.groupNote.text}</p>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
                                <a href="tel:+41912615199" className="inline-flex items-center justify-center gap-2 bg-white text-ink px-6 py-3.5 font-bold text-sm tracking-tight hover:bg-ink hover:text-white transition-colors">
                                    <Phone className="w-4 h-4" /> 091 261 51 99
                                </a>
                                <a href="mailto:backoffice@7med.ch" className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-6 py-3.5 font-bold text-sm tracking-tight hover:bg-white hover:text-brand transition-colors">
                                    Scrivici →
                                </a>
                            </div>
                        </div>
                    </div>
                </section>
            ) : null}

            {/* DOVE SI SVOLGE — da voi o nelle 4 sedi */}
            <section className="py-20 lg:py-28 bg-ink text-white" data-testid="dove-si-svolge">
                <div className="container-7m">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 mb-12 lg:mb-16">
                        <div className="lg:col-span-7">
                            <div className="label-eyebrow !text-brand">Dove si svolge</div>
                            <h2 className="mt-4 font-heading font-black text-4xl lg:text-5xl tracking-tighter leading-tight">
                                {course.venueOnSite === false ? <>Nelle nostre <span className="text-brand">4 aule.</span></> : <>Da voi <span className="text-brand">o da noi.</span></>}
                            </h2>
                        </div>
                        <p className="lg:col-span-5 text-white/80 text-readable-lg">
                            {course.venueOnSite === false ? (
                                <>Il corso si svolge esclusivamente nelle <strong className="text-white">nostre 4 aule attrezzate in Ticino</strong>. Scegli la sede più comoda per te.</>
                            ) : (
                                <>Il corso lo facciamo sia <strong className="text-white">presso la vostra sede</strong> sia nelle <strong className="text-white">nostre 4 aule in Ticino</strong>. Scegli quello che funziona meglio per il tuo team.</>
                            )}
                        </p>
                    </div>

                    <div className={`grid grid-cols-1 ${course.venueOnSite === false ? "" : "md:grid-cols-2"} gap-px bg-white/10`}>
                        {course.venueOnSite !== false && (
                            <div className="bg-ink p-8 lg:p-10" data-testid="venue-da-voi">
                                <Building2 className="w-9 h-9 text-brand" strokeWidth={1.5} />
                                <h3 className="mt-5 font-heading font-bold text-2xl lg:text-3xl tracking-tight">Da voi</h3>
                                <p className="mt-3 text-white/75 leading-relaxed">
                                    Veniamo direttamente nella vostra sede, ovunque in Ticino. Portiamo manichini, defibrillatori
                                    da training e tutto il materiale didattico. Voi pensate solo a riunire le persone.
                                </p>
                                <ul className="mt-6 space-y-2.5 text-sm">
                                    {["Zero spostamenti per il team", "Setup completo a cura nostra", "Possiamo personalizzare gli orari", "Materiale didattico col vostro logo (su richiesta)"].map((t) => (
                                        <li key={t} className="flex gap-3 items-start"><Check className="w-4 h-4 text-brand flex-shrink-0 mt-1" strokeWidth={2.5} /><span className="text-white/85">{t}</span></li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* DA NOI - 4 sedi */}
                        <div className="bg-ink p-8 lg:p-10" data-testid="venue-da-noi">
                            <MapPin className="w-9 h-9 text-brand" strokeWidth={1.5} />
                            <h3 className="mt-5 font-heading font-bold text-2xl lg:text-3xl tracking-tight">{course.venueOnSite === false ? "Le nostre 4 sedi" : "Da noi · 4 sedi"}</h3>
                            <p className="mt-3 text-white/75 leading-relaxed">
                                Aule attrezzate a Mendrisio, Lugano-Savosa, Bellinzona e Locarno. Scegli la più comoda
                                per i partecipanti.
                            </p>
                            <div className={`mt-6 grid ${course.venueOnSite === false ? "grid-cols-2 lg:grid-cols-4" : "grid-cols-2"} gap-3`}>
                                {SEDI.map((s) => (
                                    <div key={s.slug} className="border border-white/15 p-3.5 hover:border-brand transition-colors">
                                        <div className="font-heading font-bold text-base">{s.city}</div>
                                        <div className="mt-0.5 text-[12px] text-white/55 leading-snug">{s.address}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            {course.faq?.length ? (
                <section className="py-20 lg:py-28 bg-white">
                    <div className="container-7m grid grid-cols-1 lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-4">
                            <div className="label-eyebrow text-brand">FAQ</div>
                            <h2 className="mt-4 font-heading font-black text-3xl lg:text-4xl tracking-tighter leading-tight">Domande frequenti.</h2>
                            <p className="mt-6 text-ink-muted text-sm leading-relaxed">Risposte rapide ai dubbi più comuni su {course.title}. Per maggiori dettagli, contattaci.</p>
                        </div>
                        <div className="lg:col-span-8 space-y-px bg-ink/10">
                            {course.faq.map((it) => (
                                <details key={it.q} className="group bg-white p-6 lg:p-8" data-testid={`faq-${course.slug}`}>
                                    <summary className="cursor-pointer flex items-center justify-between gap-4 list-none">
                                        <span className="font-heading font-semibold text-lg leading-snug">{it.q}</span>
                                        <span className="text-brand text-2xl font-light leading-none transition-transform group-open:rotate-45">+</span>
                                    </summary>
                                    <p className="mt-4 text-ink-muted leading-relaxed text-readable">{it.a}</p>
                                </details>
                            ))}
                        </div>
                    </div>
                </section>
            ) : null}

            {/* DATE LIVE / PREVENTIVO già renderizzati sopra (subito dopo Punti chiave) */}
        </>
    );
}

function Spec({ icon: Icon, label, value, highlight = false }) {
    return (
        <div className="p-5 lg:p-6 flex items-start gap-4 bg-white">
            <Icon className="w-5 h-5 text-brand flex-shrink-0 mt-0.5" strokeWidth={1.5} />
            <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-ink-muted">{label}</div>
                <div className={`font-bold mt-1 ${highlight ? "text-brand font-heading text-xl" : "text-base"}`}>{value}</div>
            </div>
        </div>
    );
}

function ModuleItem({ module: m, index, testId }) {
    const hasDetail = !!m.description;
    const num = String(index + 1).padStart(2, "0");

    if (!hasDetail) {
        return (
            <li className="flex items-center justify-between py-5" data-testid={testId}>
                <div className="flex items-start gap-4">
                    <span className="font-mono text-xs text-ink-muted">{num}</span>
                    <span className="font-heading font-semibold text-lg">{m.name}</span>
                </div>
                {m.practical && <span className="text-[10px] font-mono uppercase tracking-[0.2em] px-2 py-1 bg-brand text-white">Pratica</span>}
                {m.theory && <span className="text-[10px] font-mono uppercase tracking-[0.2em] px-2 py-1 border border-ink">Teoria</span>}
            </li>
        );
    }

    return (
        <li data-testid={testId}>
            <details className="group">
                <summary className="cursor-pointer list-none flex items-center justify-between gap-4 py-5 hover:text-brand transition-colors">
                    <div className="flex items-start gap-4 flex-1 min-w-0">
                        <span className="font-mono text-xs text-ink-muted mt-1.5">{num}</span>
                        <span className="font-heading font-semibold text-lg leading-snug">{m.name}</span>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                        {m.practical && <span className="text-[10px] font-mono uppercase tracking-[0.2em] px-2 py-1 bg-brand text-white">Pratica</span>}
                        {m.theory && <span className="text-[10px] font-mono uppercase tracking-[0.2em] px-2 py-1 border border-ink">Teoria</span>}
                        <span className="text-brand text-2xl font-light leading-none transition-transform group-open:rotate-45 select-none">+</span>
                    </div>
                </summary>
                <div className="pb-6 pl-10 pr-2 text-ink-muted leading-relaxed text-readable whitespace-pre-line animate-in fade-in duration-300">
                    {m.description}
                </div>
            </details>
        </li>
    );
}

function FormatBlock({ format }) {
    return (
        <div className="space-y-8" data-testid="format-block">
            <p className="text-readable-lg leading-relaxed">{format.intro}</p>

            {format.stats?.length ? (
                <div className="border border-ink/10">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-ink/10">
                        {format.stats.map((s) => (
                            <div key={s.label} className="bg-white p-6 lg:p-8">
                                <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em] text-brand">
                                    <Zap className="w-3.5 h-3.5" strokeWidth={2} />
                                    {s.label}
                                </div>
                                <div className="mt-3 font-heading font-black text-3xl lg:text-4xl tracking-tighter leading-tight text-ink">{s.value}</div>
                            </div>
                        ))}
                    </div>
                    {format.upgradeNote ? (
                        <div className="bg-ink text-white p-5 lg:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="format-upgrade-note">
                            <span className="text-sm sm:text-[15px] text-white/85">{format.upgradeNote.text}</span>
                            <Link to={format.upgradeNote.linkHref} className="inline-flex items-center gap-2 text-brand font-bold text-sm hover:gap-3 transition-all whitespace-nowrap">
                                {format.upgradeNote.linkText} <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                    ) : null}
                </div>
            ) : null}

            {format.features?.length ? (
                <ul className="space-y-3 mt-4">
                    {format.features.map((f) => (
                        <li key={f} className="flex gap-3 items-start">
                            <Check className="w-5 h-5 text-brand flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                            <span className="leading-relaxed">{f}</span>
                        </li>
                    ))}
                </ul>
            ) : null}
        </div>
    );
}

function TopicsList({ topics }) {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-ink/10 border border-ink/10" data-testid="topics-list">
            {topics.map((topic, i) => (
                <div key={topic} className="bg-white p-5 lg:p-6 flex items-start gap-4 hover:bg-[#FAFAFA] transition-colors" data-testid={`topic-${i}`}>
                    <span className="font-mono text-xs text-brand mt-1">{String(i + 1).padStart(2, "0")}</span>
                    <span className="font-heading font-semibold text-base leading-snug flex-1">{topic}</span>
                    <Check className="w-4 h-4 text-brand flex-shrink-0 mt-1.5" strokeWidth={2.5} />
                </div>
            ))}
        </div>
    );
}
